import React, { useEffect, useState } from 'react';
import { Button, Input, Select, DatePicker, message, Upload } from 'antd';
import jaJP from 'antd/es/date-picker/locale/ja_JP';
import dayjs from 'dayjs';
import './detail-index.scss';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../store';
import { createVoucher, deleteVoucher, findVoucherById, updateVoucher } from '../../../../store/vouchers/voucherSlice';
import { useNavigate, useParams } from 'react-router-dom';
import ModalDelete from '../../../../components/Modal/ModalDelete';

const { TextArea } = Input;

const VoucherDetail: React.FC = () => {
  const voucherSlice = useSelector((state: RootState) => state.voucher);
  const { detail } = voucherSlice;
  const { id } = useParams<{ id: string }>();
  const [voucherCode, setVoucherCode] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [description, setDescription] = useState('');
  const [expiryDate, setExpiryDate] = useState<any>(null);
  const [status, setStatus] = useState('使用可能');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fileName, setFileName] = useState<string>('');
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      dispatch(findVoucherById({ id: id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (detail && id) {
      setVoucherCode(detail.name);
      setFileName(detail.img ? detail.img.split('/').pop() || '' : '');
      setImageUrl(detail.img);
      setDescription(detail.content);
      setExpiryDate(detail.expDate ? dayjs(detail.expDate) : null);
      setStatus(detail.status === '使用可能' ? '使用可能' : '期限切れ');
    }
  }, [id, detail]);

  const handleSave = async () => {
    if (!id && !imageUrl) {
      message.error('画像を選択してください。');
      return;
    }
    if (id && !imageUrl) {
      message.error('画像を選択してください。');
      return;
    }
    if (!voucherCode.trim()) {
      message.error('バウチャーコードを入力してください。');
      return;
    }
    if (!description.trim()) {
      message.error('内容を入力してください。');
      return;
    }
    if (!expiryDate) {
      message.error('期限を選択してください。');
      return;
    }
    const formData = new FormData();
    formData.append('name', voucherCode);
    formData.append('content', description);
    formData.append('expDate', expiryDate?.format('YYYY/MM/DD'));
    formData.append('status', status);
    if (imageUrl) {
      formData.append('image', imageUrl);
    }
    if (id) {
      formData.append('id', id);
      const result = await dispatch(updateVoucher(formData));
      if (result.meta.requestStatus === 'fulfilled') {
        message.success('更新が成功しました!');
        navigate('/e-voucher');
      } else {
        message.error('更新に失敗しました。もう一度お試しください。');
      }
    } else {
      const result = await dispatch(createVoucher(formData));
      if (result.meta.requestStatus === 'fulfilled') {
        message.success('新規作成が成功しました!');
        navigate('/e-voucher');
      } else {
        message.error('新規作成に失敗しました。もう一度お試しください!');
      }
    }
  };

  const handleDelete = async () => {
    if (id) {
      try {
        const result = await dispatch(deleteVoucher(id));
        if (result.meta.requestStatus === 'fulfilled') {
          message.success('削除が成功しました!');
          navigate('/e-voucher');
        } else {
          message.error('バウチャーは使用中のため、削除できません。');
        }
      } catch (error) {
        message.error('削除中にエラーが発生しました。');
      } finally {
        setIsModalOpen(false);
      }
    }
  };

  const showDeleteModal = () => {
    setIsModalOpen(true);
  };
  const handleCancel = () => {
    setIsModalOpen(false);
  };
  const handleBack = () => {
    window.history.back();
  };

  const handleUploadChange = async ({ fileList: newFileList }) => {
    const latestFile = newFileList[newFileList.length - 1];
    if (latestFile && latestFile.originFileObj) {
      setImageUrl(latestFile.originFileObj);
      setFileName(latestFile.name);
    } else {
      setFileName('');
    }
  };
  return (
    <div className="voucher-detail">
      <div className="header">
        <Button className="back-button" onClick={handleBack}>
          ←
        </Button>
        <Button className="save-button" type="primary" onClick={handleSave}>
          保存
        </Button>
      </div>
      <div className="section-title">
        バウチャー情報
        {id && (
          <Button
            type="text"
            icon={
              <img src={`${process.env.PUBLIC_URL}/assets/icons/delete.png`} alt="Delete Logo" className="icon-table" />
            }
            danger
            onClick={() => showDeleteModal()}
          />
        )}
      </div>
      <div className="form-container">
        <div className="form-group">
          <label>バウチャーコード</label>
          <Input placeholder="コンテンツを入力" value={voucherCode} onChange={(e) => setVoucherCode(e.target.value)} />
        </div>
        <div className="form-group">
          <label>表示画像</label>
          <div className="custom-file-input">
            <Upload
              name="image"
              accept=".png,.jpg,.jpeg,.gif,.bmp,.webp,.svg"
              listType="picture"
              maxCount={1}
              showUploadList={false}
              id="fileInput"
              style={{ display: 'none' }}
              onChange={handleUploadChange}
              beforeUpload={(file) => {
                const isImage = file.type.startsWith('image/');
                if (!isImage) {
                  message.error('画像ファイルのみアップロード可能です。');
                }
                const isLt2M = file.size / 1024 / 1024 < 2;
                if (!isLt2M) {
                  message.error('画像サイズは2MB以下である必要があります。');
                }
                return false;
              }}
            >
              <Button>ファイルを選択</Button>
            </Upload>
            <span className="file-name">
              {fileName ? `選択されたファイル: ${fileName}` : 'ファイルが選択されていません'}
            </span>
          </div>
        </div>
        <div className="form-group">
          <label>内容</label>
          <TextArea
            placeholder="コンテンツを入力"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="form-group-row">
          <div className="form-group">
            <label>期限</label>
            <DatePicker
              locale={jaJP}
              placeholder="YYYY/MM/DD"
              style={{ width: '100%' }}
              value={expiryDate}
              onChange={(date) => setExpiryDate(date)}
              disabledDate={(current) => current && current.toDate().getTime() < new Date().setHours(0, 0, 0, 0)}
            />
          </div>
          <div className="form-group">
            <label>ステータス</label>
            <Select
              style={{ width: '100%' }}
              value={status}
              onChange={(value) => setStatus(value)}
              options={[
                { value: '使用可能', label: '使用可能' },
                { value: '期限切れ', label: '期限切れ' },
              ]}
            />
          </div>
        </div>
      </div>
      <ModalDelete
        isOpen={isModalOpen}
        onOk={handleDelete}
        onCancel={handleCancel}
        content={`「バウチャー」を削除してもよろしいですか?`}
      />
    </div>
  );
};

export default VoucherDetail;
